import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Type, ImagePlus, Loader2, Send, X, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  analyzeImage,
  analyzeText,
  fetchUsage,
  fileToBase64,
} from "@/lib/api";
import ResultCard from "@/components/ResultCard";
import ShareSection from "@/components/ShareSection";

const MAX_TEXT = 4000;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const DAILY_LIMIT = 3;

// Pure helper — returns badge styling + text for the current quota state.
// Extracted to avoid a nested ternary in JSX (improves readability and testability).
function getLimitBadge(remaining) {
  if (remaining <= 0) {
    return {
      className: "bg-red-50 text-red-800",
      text: "Hai esaurito i controlli di oggi. Riprova domani.",
    };
  }
  const wordSingular = remaining === 1;
  if (wordSingular) {
    return {
      className: "bg-yellow-50 text-yellow-800",
      text: "Hai 1 controllo rimasto oggi",
    };
  }
  return {
    className: "bg-green-50 text-green-800",
    text: `Hai ${remaining} controlli rimasti oggi`,
  };
}

export default function VerificationTool() {
  const [mode, setMode] = useState("testo"); // "testo" | "foto"
  const [text, setText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState({ used: 0, remaining: DAILY_LIMIT, limit: DAILY_LIMIT });
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    // fetchUsage is a stable module import; setUsage is stable from useState.
    // No reactive deps are needed.
    fetchUsage().then(setUsage).catch(() => {});
  }, []);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Per favore carica solo una foto (immagine).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("L'immagine è troppo grande. Massimo 8 MB.");
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setImageBase64(b64);
      setImagePreview(b64);
    } catch {
      toast.error("Impossibile leggere l'immagine.");
    }
  };

  const onPickFile = (e) => handleFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const resetImage = () => {
    setImageBase64(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (usage.remaining <= 0) {
      toast.error("Hai esaurito le 3 verifiche giornaliere. Torna domani.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      let res;
      if (mode === "testo") {
        if (!text.trim()) {
          toast.error("Per favore scrivi o incolla il messaggio da verificare.");
          setLoading(false);
          return;
        }
        res = await analyzeText(text.trim());
      } else {
        if (!imageBase64) {
          toast.error("Per favore carica prima una foto del messaggio.");
          setLoading(false);
          return;
        }
        res = await analyzeImage(imageBase64);
      }
      setResult(res);
      setUsage((u) => ({ ...u, used: u.used + 1, remaining: res.remaining }));
      toast.success("Analisi completata!");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Si è verificato un errore. Riprova tra poco.";
      toast.error(msg);
      if (e?.response?.status === 429) {
        setUsage((u) => ({ ...u, remaining: 0, used: u.limit }));
      }
    } finally {
      setLoading(false);
    }
  };

  const badge = getLimitBadge(usage.remaining);

  return (
    <div className="w-full max-w-4xl mx-auto" data-testid="verification-tool">
      <div className="bg-white rounded-[2rem] shadow-[0_8px_40px_rgb(30,58,138,0.08)] border-4 border-blue-50 p-6 md:p-12">
        {/* Mode tabs */}
        <div className="flex w-full bg-blue-50 rounded-2xl p-2 mb-8 gap-2">
          <button
            type="button"
            data-testid="tab-testo"
            onClick={() => setMode("testo")}
            className={`flex-1 flex items-center justify-center gap-3 rounded-xl py-4 text-lg md:text-xl font-bold transition-all ${
              mode === "testo"
                ? "bg-white text-[#1E3A8A] shadow-md"
                : "text-gray-600 hover:text-[#1E3A8A]"
            }`}
          >
            <Type size={24} /> Incolla il Testo
          </button>
          <button
            type="button"
            data-testid="tab-foto"
            onClick={() => setMode("foto")}
            className={`flex-1 flex items-center justify-center gap-3 rounded-xl py-4 text-lg md:text-xl font-bold transition-all ${
              mode === "foto"
                ? "bg-white text-[#1E3A8A] shadow-md"
                : "text-gray-600 hover:text-[#1E3A8A]"
            }`}
          >
            <ImagePlus size={24} /> Carica una Foto
          </button>
        </div>

        {/* Input area */}
        {mode === "testo" ? (
          <div>
            <label
              htmlFor="message-text"
              className="block text-lg md:text-xl font-semibold text-[#1E3A8A] mb-3"
            >
              Incolla qui sotto il messaggio che hai ricevuto:
            </label>
            <textarea
              id="message-text"
              data-testid="textarea-message"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
              placeholder="Esempio: Ciao nonno, sono tua nipote, ho perso il telefono e ti scrivo dal numero di un'amica. Mi puoi mandare 200 euro su questo IBAN..."
              className="w-full min-h-[220px] p-5 md:p-6 text-lg md:text-xl rounded-2xl border-4 border-gray-200 focus:border-[#1E3A8A] focus:outline-none resize-y bg-gray-50 placeholder-gray-400 font-body leading-relaxed"
            />
            <div className="mt-2 text-sm text-gray-500 text-right">
              {text.length} / {MAX_TEXT} caratteri
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-lg md:text-xl font-semibold text-[#1E3A8A] mb-3">
              Carica una foto (screenshot) del messaggio:
            </label>
            {!imagePreview ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                data-testid="dropzone-image"
                className="border-4 border-dashed border-gray-300 hover:border-[#1E3A8A] rounded-2xl p-8 md:p-12 text-center bg-gray-50 cursor-pointer transition-colors flex flex-col items-center justify-center gap-4 min-h-[260px]"
              >
                <ImagePlus size={64} className="text-[#1E3A8A]" />
                <p className="text-xl md:text-2xl font-semibold text-[#1E3A8A]">
                  Tocca qui per caricare una foto
                </p>
                <p className="text-base md:text-lg text-gray-600">
                  Puoi anche trascinare l'immagine in questo riquadro
                </p>
                <p className="text-sm text-gray-500">
                  Formati ammessi: JPG, PNG, WEBP — massimo 8 MB
                </p>
              </div>
            ) : (
              <div className="relative rounded-2xl overflow-hidden border-4 border-gray-200">
                <img
                  src={imagePreview}
                  alt="Anteprima messaggio"
                  className="w-full max-h-[480px] object-contain bg-gray-100"
                  data-testid="image-preview"
                />
                <button
                  type="button"
                  data-testid="remove-image-button"
                  onClick={resetImage}
                  className="absolute top-3 right-3 bg-white/95 hover:bg-white text-[#DC2626] rounded-full p-2 shadow-lg"
                  aria-label="Rimuovi immagine"
                >
                  <X size={24} />
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              ref={fileInputRef}
              onChange={onPickFile}
              className="hidden"
              data-testid="file-input"
            />
          </div>
        )}

        {/* Submit */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            type="button"
            data-testid="analyze-button"
            onClick={submit}
            disabled={loading || usage.remaining <= 0}
            className="w-full md:w-auto bg-[#1E3A8A] text-white hover:bg-[#152C69] disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[68px] px-12 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-4 text-xl md:text-2xl font-bold"
          >
            {loading ? (
              <>
                <Loader2 size={28} className="animate-spin" />
                Sto controllando...
              </>
            ) : (
              <>
                <ShieldCheck size={28} />
                Verifica adesso
                <Send size={22} />
              </>
            )}
          </button>

          <div
            data-testid="daily-limit-badge"
            className={`text-center text-lg font-semibold rounded-full py-2 px-6 inline-block ${badge.className}`}
          >
            {badge.text}
          </div>

          {/* EU AI Act transparency — Art. 50: user must be clearly informed
              they're interacting with an AI system before submitting. */}
          <div
            data-testid="ai-notice"
            className="mt-2 max-w-2xl text-center text-sm text-gray-500 leading-relaxed"
          >
            <Sparkles size={14} className="inline -mt-1 mr-1 text-[#1E3A8A]" />
            Analisi automatica con intelligenza artificiale (Google Gemini 3).
            Non sostituisce il parere di un familiare di fiducia o della Polizia Postale.{" "}
            <Link to="/trasparenza-ai" data-testid="ai-notice-link" className="text-[#1E3A8A] underline">
              Maggiori informazioni
            </Link>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div ref={resultRef} className="mt-10 space-y-8">
          <ResultCard result={result} />
          <ShareSection variant="compact" />
        </div>
      )}
    </div>
  );
}
